import { useRef, useState } from 'react';
import ReactCrop, { type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { Button } from '@/components/Button/Button';
import { cropImageToBlob } from '@/lib/image/cropImage';

import styles from './ImageCropper.module.css';

interface ImageCropperProps {
  imageUrl: string;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
  onSkip: () => void;
}

export function ImageCropper({
  imageUrl,
  onCancel,
  onConfirm,
  onSkip,
}: ImageCropperProps) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<PixelCrop>();
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    if (!crop || !imageRef.current) {
      return;
    }

    try {
      const blob = await cropImageToBlob(imageRef.current, crop);
      onConfirm(blob);
    } catch (cropError) {
      setError(
        cropError instanceof Error
          ? cropError.message
          : 'Unable to prepare the image crop.',
      );
    }
  }

  return (
    <div className={styles.root}>
      <div>
        <h4 className={styles.title}>Crop your image (optional)</h4>
        <p className={styles.help}>
          Drag to choose the part of the image you want KAI to grade, or use the
          full image.
        </p>
      </div>
      <div className={styles.cropFrame}>
        <ReactCrop crop={crop} onChange={(nextCrop) => setCrop(nextCrop)}>
          <img
            alt="Selected work to crop"
            className={styles.image}
            ref={imageRef}
            src={imageUrl}
          />
        </ReactCrop>
      </div>
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button onClick={onCancel} variant="ghost">
          Remove
        </Button>
        <Button onClick={onSkip} variant="secondary">
          Use Full Image
        </Button>
        <Button disabled={!crop?.width || !crop?.height} onClick={() => void handleConfirm()}>
          Confirm
        </Button>
      </div>
    </div>
  );
}
